import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackCommentProps,
  UserType,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  FeedbackBySections,
  SelectedFeedback,
  AllVerificationsProgress,
  VerificationProgress,
  INDICATOR_DETAILS_QUESTION_ID,
  IMPACT_SUMMARY_QUESTION_ID,
  scrollToTopOnFeedback,
} from './utils';
import {
  deleteQuestionLevelComment,
  updateQuestionLevelComment,
} from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  FeedbackApi,
  FeedbackStatus,
} from 'donor/portal/pages/FeedbackDashboard/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import { useClient } from 'shared/apollo';
import { markAsResolvedOrVerified } from './api';
import {
  FeedbackQuestion,
  FeedbackQuestionsById,
  getQuestion,
  mapFeedbackQuestions,
} from './MakeEdits/api';
import FeedbackDetails from './FeedbackDetails';
import { updateAnswerState } from '../ReportBuilder/Components/ReportFinalize/utils';
import { getQuestionText } from 'admin/pages/ManageAccounts/Guidance/disabledQuestions';

const getUserType = (type: UserType): UserType => {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/donor')) {
    return 'donor';
  }
  if (pathname.startsWith('/npo')) {
    return 'npo';
  }
  if (pathname.startsWith('/account')) {
    return 'ti staff';
  }
  return type;
};

const getNewState = (
  userType: UserType,
  current: VerificationProgress | undefined = {},
  date: string,
): VerificationProgress => {
  const type = getUserType(userType);
  switch (type) {
    case 'ti staff':
      return {
        ...current,
        tiVerified: date,
      };
    case 'donor':
      return {
        ...current,
        donorVerified: date,
      };
    case 'npo':
      return {
        ...current,
        resolved: date,
      };

    default:
      return current;
  }
};

interface FeedbackSectionSelectedProps {
  total: number;
  reportYear?: string;
  isSectionCompleted: boolean;
  selectedIndex: number | undefined;
  selectedSection: SelectedFeedback;
  feedbackSections: FeedbackBySections;
  reportId: number;
  programName: string;
  currentUserId?: string;
  currentUserEmail?: string;
  userName: string;
  userType: UserType;
  report: FeedbackApi;
  feedbackStatus: FeedbackStatus;
  handleNext: () => unknown;
  reloadReportAnswers?: () => unknown;
  setProgress: React.Dispatch<React.SetStateAction<AllVerificationsProgress>>;
  handleDeleteComment: (selected: SelectedFeedback, index: number) => unknown;
  handleAddComment: (
    selected: SelectedFeedback,
    comment: FeedbackCommentProps,
  ) => unknown;
  handleUpdateComment: (
    selected: SelectedFeedback,
    index: number,
    comment: FeedbackCommentProps,
  ) => unknown;
}

const StyledDiv = styled.div`
  .feedback-item-count {
    margin-top: 27px;
  }
  .feedback-section-name {
    font-weight: normal;
  }
`;

const FeedbackSectionSelected: React.FC<FeedbackSectionSelectedProps> = ({
  total,
  programName,
  selectedSection,
  feedbackSections,
  reportId,
  reportYear,
  report,
  currentUserId,
  currentUserEmail,
  userName,
  userType,
  selectedIndex,
  feedbackStatus,
  isSectionCompleted,
  handleNext,
  setProgress,
  handleAddComment,
  handleDeleteComment,
  handleUpdateComment,
  reloadReportAnswers,
}) => {
  const client = useClient();
  const [requestState, makeRequest, setRequest] = useGraphQLRequest<unknown>();
  const [questions, setQuestions] = useState<FeedbackQuestionsById | undefined>(
    undefined,
  );
  const [requestQuestionState, makeQuestionRequest] = useGraphQLRequest<{
    allNpoReportQuestions: FeedbackQuestion[];
  }>();

  const handleAnswerUpdate = useCallback(
    (
      questionId: number,
      answer: string,
      questionText: string,
      questionType: FeedbackQuestion['questionType'],
    ) => {
      setQuestions(q => {
        return updateAnswerState({
          answer: answer,
          questionId,
          questionText,
          questions: q,
          questionType,
        });
      });
    },
    [setQuestions],
  );

  const reloadAnswers = useCallback(() => {
    makeQuestionRequest(
      getQuestion(client!, reportId, selectedSection.section.sectionId),
    );
    reloadReportAnswers?.();
  }, [makeQuestionRequest, reloadReportAnswers, reportId, selectedSection]);

  useEffect(() => {
    reloadAnswers();
    scrollToTopOnFeedback();
  }, [selectedSection.section.sectionId]);

  useEffect(() => {
    if (requestQuestionState.type !== 'REQUEST_SUCCESS') return;
    setQuestions(
      mapFeedbackQuestions(requestQuestionState.data.allNpoReportQuestions),
    );
  }, [requestQuestionState, setQuestions]);

  useEffect(() => {
    if (requestState.type !== 'REQUEST_SUCCESS') return;
    setProgress(s => {
      const newState = { ...s };
      const date = new Date().toDateString();
      if (selectedSection.question) {
        newState[selectedSection.question.questionId] = getNewState(
          userType,
          newState[selectedSection.question.questionId],
          date,
        );
      } else {
        newState[selectedSection.section.sectionId] = getNewState(
          userType,
          newState[selectedSection.section.sectionId],
          date,
        );
      }
      return newState;
    });
    handleNext();
    setRequest(requestInit());
  }, [
    requestState,
    selectedSection,
    userType,
    setProgress,
    handleNext,
    setRequest,
  ]);

  const markAsVerified = useCallback(() => {
    makeRequest(
      markAsResolvedOrVerified(
        client!,
        reportId,
        userType,
        selectedSection,
        feedbackStatus,
      ),
    );
  }, [reportId, userType, selectedSection, feedbackStatus, makeRequest]);

  const section = selectedSection.section;
  const selectedFeedbackSection = feedbackSections[section.sectionId];

  if (!selectedFeedbackSection) return null;

  const defaultQuestionText = getQuestionText(
    selectedSection.question?.questionId,
    selectedSection.question?.questionText,
  );
  const questionText = defaultQuestionText
    ? defaultQuestionText
    : selectedSection.question
    ? questions
      ? questions[selectedSection.question.questionId]?.answers[0]
          ?.questionText || questions[selectedSection.question.questionId]?.text
      : selectedSection.question.questionText
    : undefined;

  const feedback = selectedSection.question
    ? selectedFeedbackSection.feedbackByQuestionId[
        selectedSection.question.questionId
      ]
    : selectedFeedbackSection.overAllSection;

  const allowEdits: boolean =
    (feedbackStatus === 'TI Feedback Provided' ||
      feedbackStatus === 'Funder Feedback Provided') &&
    Boolean(selectedSection.question);

  return (
    <StyledDiv>
      <TrueImpactText variant="heading-label" className="feedback-item-count">
        FEEDBACK ITEM {(selectedIndex || 0) + 1} OF {total}
      </TrueImpactText>
      <TrueImpactText variant="heading">
        Section: <span className="feedback-section-name">{section.label}</span>
      </TrueImpactText>
      {selectedSection.question &&
        selectedSection.question.questionId !== INDICATOR_DETAILS_QUESTION_ID &&
        selectedSection.question.questionId !== IMPACT_SUMMARY_QUESTION_ID && (
          <TrueImpactText variant="heading-subheading">
            {questionText}
          </TrueImpactText>
        )}
      <FeedbackDetails
        report={report}
        reportId={reportId}
        reportYear={reportYear}
        feedback={feedback}
        questions={questions}
        allowEdits={allowEdits}
        currentUserName={userName}
        currentUserType={userType}
        programName={programName}
        requestState={requestState}
        currentUserId={currentUserId}
        feedbackStatus={feedbackStatus}
        selectedSection={selectedSection}
        currentUserEmail={currentUserEmail}
        isSectionCompleted={isSectionCompleted}
        feedbackByQuestionId={selectedFeedbackSection.feedbackByQuestionId}
        handleAnswerUpdate={handleAnswerUpdate}
        loadingAnswers={
          requestQuestionState.type === 'REQUEST_INIT' ||
          requestQuestionState.type === 'REQUEST_START'
        }
        markAsVerified={markAsVerified}
        reloadAnswers={reloadAnswers}
        handleAddComment={handleAddComment}
        handleUpdateComment={handleUpdateComment}
        handleDeleteComment={handleDeleteComment}
        updateQuestionLevelComment={updateQuestionLevelComment}
        deleteQuestionLevelComment={deleteQuestionLevelComment}
      />
    </StyledDiv>
  );
};

export default FeedbackSectionSelected;
